import { AbstractControl, ValidationErrors, AsyncValidatorFn } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged, switchMap, startWith, take } from 'rxjs/operators';
import { PromptModuleApiService } from '@vendasta/ai-assistants';

export function urlValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return null;
    }
    return { invalidHttpUrl: true };
  } catch {
    return { invalidHttpUrl: true };
  }
}

// Create async validator for template validation
export function createTemplateValidator(promptModuleApiService: PromptModuleApiService): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const content = control.value;

    // Don't validate empty content
    if (!content || content.trim() === '') {
      return of(null);
    }

    // Only validate if the user has interacted with this field
    // This prevents validation during initial form construction
    if (!control.touched && !control.dirty) {
      return of(null);
    }

    // Use the control's valueChanges to get proper debouncing across keystrokes
    return control.valueChanges.pipe(
      startWith(content), // Include the current value
      debounceTime(500), // Debounce to avoid excessive API calls
      distinctUntilChanged(),
      switchMap((value) => {
        // Skip validation for empty content
        if (!value || value.trim() === '') {
          return of(null);
        }

        return promptModuleApiService
          .validate({
            content: value,
          })
          .pipe(
            map((response) => {
              if (response.isValid) {
                return null;
              } else {
                return {
                  templateSyntax: true,
                };
              }
            }),
            catchError(() => {
              return of(null);
            }),
          );
      }),
      take(1),
    );
  };
}
